import type RootStore from 'stores/RootStore';
import { RouteType } from 'types/RouteType';
import UserManagementStore from './UserManagementStore';

/**
 * Administration → User Management.
 *
 * The body is a hand-rolled responsive list (cards on phones, a table on
 * desktop) rather than the shared inline-edit data grid: that grid's fixed
 * column widths forced a horizontal scroll on mobile and it had no room for
 * per-account actions. Editing now happens in the UserEditDialog and passwords
 * in the ResetPasswordDialog, both of which go full-screen on a phone via the
 * router's forceFullScreenDialog.
 */

const storeOf = (rootStore: RootStore) =>
  rootStore.userManagementStore as UserManagementStore;

/**
 * A failed write THROWS: the dialog framework keeps a dialog open when its
 * onSubmit rejects and shows the message in the footer, so the API's error
 * (duplicate email, password policy) stays on screen with the form intact.
 * Resolving closes it. (This used to resolve and re-open the dialog a tick
 * later, which flashed it shut and cleared the footer's error.)
 */
const submitOrThrow = async (
  rootStore: RootStore,
  submit: (store: UserManagementStore) => Promise<boolean>,
  errorOf: (store: UserManagementStore) => string | null,
  fallback: string
) => {
  const store = storeOf(rootStore);
  if (!(await submit(store))) throw new Error(errorOf(store) || fallback);
};

const userManagementRoute: RouteType[] = [
  {
    name: 'User Management',
    path: '/user-management',
    layout: '/app',
    viewportCard: {
      header: {
        title: 'User Management',
        component: () => import('./UserManagement.Header'),
        enableRefreshButton: true,
        enableBackButton: true,
      },
      body: {
        component: () => import('./UserManagement.Body'),
      },
    },
    group: 'Admin',
    searchAliases: ['users', 'accounts', 'employees', 'add user'],
    hidden: false,
    requiresAuth: true,
    requiresPermission: false,
    requiresAdmin: true,
    enableAutoFind: false,
    channels: ['user', 'departments', 'roles'],
    onEnter: async ({ rootStore }) => {
      const store = rootStore.userManagementStore as UserManagementStore | undefined;

      // The user list loads through the view store so a failure is RECORDED
      // (loadError → error + Retry) instead of swallowed by the ServiceStore
      // and rendered as "No user accounts yet".
      void store?.load();
      // Feeds the Add/Edit User department picker. Tracked as loading, loaded
      // or failed (departmentPicker): the picker is disabled until it has
      // loaded and shows the failure rather than made-up names. The user list
      // itself is unaffected either way.
      void store?.loadDepartments();

      // Populate the Add/Edit User role picker from the `roles` table, so a
      // business's own roles appear alongside the built-in ones. Failure is
      // non-fatal — the picker falls back to the seeded names.
      void store?.loadRoles?.();
    },
    dialogs: [
      {
        name: 'UserFormDialog',
        title: 'Add User',
        component: () => import('./dialogs/UserFormDialog.Body'),
        cancelButton: true,
        height: 640,
        submitButtonText: 'Create User',
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
          submitOrThrow(
            rootStore,
            (store) => store.saveUser(),
            (store) => store.formError,
            'The user could not be created.'
          ),
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.userManagementStore as UserManagementStore;
          return store.isFormValid && !store.saving;
        },
      },
      {
        // Same body as the create dialog — the framework's dialog title and
        // submit label are static strings, so create and edit are registered
        // separately rather than re-labelled at runtime.
        name: 'UserEditDialog',
        title: 'Edit User',
        component: () => import('./dialogs/UserFormDialog.Body'),
        cancelButton: true,
        height: 640,
        submitButtonText: 'Save Changes',
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
          submitOrThrow(
            rootStore,
            (store) => store.saveUser(),
            (store) => store.formError,
            'The user could not be saved.'
          ),
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.userManagementStore as UserManagementStore;
          return store.isFormValid && !store.saving;
        },
      },
      {
        name: 'ResetPasswordDialog',
        title: 'Reset Password',
        component: () => import('./dialogs/ResetPasswordDialog.Body'),
        cancelButton: true,
        maxWidth: 'sm',
        height: 520,
        submitButtonText: 'Set Password',
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
          submitOrThrow(
            rootStore,
            (store) => store.submitPasswordReset(),
            (store) => store.resetError,
            'The password could not be set.'
          ),
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.userManagementStore as UserManagementStore;
          return store.isResetValid;
        },
      },
      {
        // Hard delete. The framework renders every submit button in primary
        // blue with no destructive variant, so the deliberate second action is
        // an acknowledgement checkbox in the body, gating isRemoveValid.
        name: 'RemoveUserDialog',
        title: 'Remove User',
        component: () => import('./dialogs/RemoveUserDialog.Body'),
        cancelButton: true,
        maxWidth: 'sm',
        height: 520,
        submitButtonText: 'Remove User',
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
          submitOrThrow(
            rootStore,
            (store) => store.removeUser(),
            (store) => store.removeError,
            'The user could not be removed.'
          ),
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.userManagementStore as UserManagementStore;
          return store.isRemoveValid;
        },
      },
    ],
  },
];

export default userManagementRoute;
